import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { router } from "@inertiajs/react"
import { useState } from "react"

const NO_PROJECT = "none"

interface NewBoardDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projects: { id: number; name: string }[]
}

function suggestKey(name: string) {
  const letters = name
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .trim()
    .split(/\s+/)
  const initials = letters.length > 1 ? letters.map((word) => word[0]).join("") : (letters[0] ?? "")
  return initials.slice(0, 4).toUpperCase()
}

export function NewBoardDialog({ open, onOpenChange, projects }: NewBoardDialogProps) {
  const [name, setName] = useState("")
  const [key, setKey] = useState("")
  const [keyTouched, setKeyTouched] = useState(false)
  const [description, setDescription] = useState("")
  const [projectId, setProjectId] = useState(NO_PROJECT)

  const onNameChange = (value: string) => {
    setName(value)
    if (!keyTouched) setKey(suggestKey(value))
  }

  const submit = () => {
    if (!name.trim() || key.trim().length < 2) return

    router.post(
      "/boards",
      {
        board: {
          name,
          key,
          description,
          project_id: projectId === NO_PROJECT ? null : Number(projectId),
        },
      },
      { onSuccess: () => onOpenChange(false) },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New board</DialogTitle>
          <DialogDescription>
            The key prefixes every ticket on the board, so OPS gives you OPS-1, OPS-2 and so on.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="board_name">Name</Label>
          <Input id="board_name" value={name} autoFocus onChange={(event) => onNameChange(event.target.value)} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="board_key">Key</Label>
          <Input
            id="board_key"
            value={key}
            maxLength={10}
            className="font-mono uppercase"
            onChange={(event) => {
              setKeyTouched(true)
              setKey(event.target.value.toUpperCase())
            }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="board_project">Project</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger id="board_project">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PROJECT}>Not tied to a project</SelectItem>
              {projects.map((project) => (
                <SelectItem key={project.id} value={String(project.id)}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="board_description">Description</Label>
          <Textarea
            id="board_description"
            value={description}
            rows={2}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || key.trim().length < 2}>
            Create board
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
